import * as React from 'react';
// Import services.
import {
	AddressService,
} from '@app/services';
// Import material ui modules.
import {
	ArrowForward,
	ArrowBack,
	Check,
} from '@mui/icons-material';
// Import hooks.
import {
	useAddressVerification,
	UseAddressVerificationResults,
} from '@app/hooks/view';
import {
	UseInputAddressState,
	useInputEmail,
	UseInputEmailResults,
	UseInputFormResult,
	useInputPhone,
	UseInputPhoneResults,
	useInputText,
	UseInputTextResults,
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupConfirmationProps,
	FormProps,
	NavigationFooterProps,
	FormInsuredInfoProps,
	ScrollbarProps,
	StatusProps,
	SubformInsuredInfoContactProps,
	SubformInsuredInfoMailingAddressProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	BroadLineOfBusiness,
	NavigationFooterAlignment,
    PhoneContactType,
} from '@app/enumerations';
// Import api models.
import {
	Address,
	InsuredInfo,
    InsuredName,
    PhoneNumber,
} from '@app/models/api';

interface UseFormInsuredInfoParameters {
	id: string;
	props: FormInsuredInfoProps,
}

interface UseFormInsuredInfoResults {
	areAllInputsDisabled: boolean;
	isPropertyAddressUsedAsMailingAddress: boolean;
	doesPropertyAddressExist: boolean;
	propertyAddressAsMailingAddressInput: UseInputYesNoResults;
	addressVerification: UseAddressVerificationResults;
	submitForm: () => void;
	applyFormProps: () => FormProps & ScrollbarProps;
	applySubformInsuredInfoMailingAddressProps: () => SubformInsuredInfoMailingAddressProps;
	applySubformInsuredInfoContactProps: () => SubformInsuredInfoContactProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	applySubmissionFormPopupConfirmationProps: () => FormPopupConfirmationProps;
}

const useFormInsuredInfo = (params: UseFormInsuredInfoParameters): UseFormInsuredInfoResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			if (requireConfirmationOnSave) {
				setOpenSaveConfirmationPopup(true);
			}
			else {
				proceedWithFormSubmission();
			}			
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Insured Information',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: false,
			statusProps: applyStatusProps(),
		};
	};

	const applySubformInsuredInfoMailingAddressProps = (): SubformInsuredInfoMailingAddressProps => {
		return {
			showInsuredAddressInput,
			isPropertyAddressUsedAsMailingAddress,
			doesPropertyAddressExist,
			propertyAddressAsMailingAddressInput,
			addressVerification,
		};
	}

	const applySubformInsuredInfoContactProps = (): SubformInsuredInfoContactProps => {
		return {
			showInsuredNameInput,
			showInsuredLastInput,
			showInsuredHomePhoneInput,
			showInsuredMobilePhoneInput,
			showInsuredEmailInput,
			showSendPaymentReceiptToEmailInput,
			isInsuredLastNameRequired,
			insuredNameInput,
			insuredLastInput,
			insuredHomePhoneInput,
			insuredMobilePhoneInput,
			insuredEmailInput,
			sendPaymentReceiptToEmailInput,
		};
	}

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		let isSaveRequired: boolean = doesFormRequireSave();
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Back',
			leftButtonClick: params.props.onBack,
			leftButtonIcon: ArrowBack,
			rightButtonDisabled: addressVerification.isAddressVerificationRequired() || addressVerification.isSingleLineAddressVerificationRequired(),
			rightButtonLabel: isSaveRequired ? 'Save' : 'Continue',
			rightButtonClick: isSaveRequired ? submitForm : params.props.onForward,
			rightButtonIcon: isSaveRequired ? Check : ArrowForward,
			alignment: NavigationFooterAlignment.SPACE_BETWEEN,
		};
	};

	/**
	 * Returns props for the submission confirmation popup form. 
	 **/
	const applySubmissionFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openSaveConfirmationPopup,
			onClose: cancelConfirmSubmission,
			onConfirm: confirmSubmission,
			message: params.props.confirmationSaveMessage ?? String.empty(),
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Returns props for the status. 
	 **/
	const applyStatusProps = (): StatusProps | undefined => {
		let statusProps: StatusProps | undefined = undefined;
		if (showStatusMessage) {
			statusProps = {
				statusMessage: params.props.statusMessage ?? String.empty(),
				statusType: AppThemeColor.ERROR,
			}
		}

		return statusProps;
	}

	const proceedWithFormSubmission = (): void => {
		if (isPropertyAddressUsedAsMailingAddress) {
			// If property address is to be used as the mailing address, then proceed forward to use the property address as the mailing address.
			params.props.onSaveInsuredInfo(createInsuredInfoWithPropertyAddress());
		}
		else if (addressVerification.addressLocationInput.doesInputHaveChanges
			|| (alwaysSaveAddressOnSubmit && !addressVerification.addressLocationInput.isDisabled)) {
			// If any changes were made to the mailing address, proceed to verify the address.
			submitFormWithAddressVerification();
		}
		else {
			// If no changes have been made to the address, then proceed to save the insured information.
			params.props.onSaveInsuredInfo(createInsuredInfo());
		}
	}

	/**
	 * Starts the submission process without validation for the address. 
	 **/
	const submitFormWithAddressVerification = (): void => {
		// Proceed forward if form validates successfully.
		try {
			if (addressVerification.addressLocationInput.value.address !== undefined) {
				// If the input address components are already provided, then we need to verify them as address components.
				addressVerification
					.verifyAddressAsComponentsAsync(addressVerification.addressLocationInput.value.address);
			}
			else if (!String.isNullOrWhiteSpace(addressVerification.addressLocationInput.value.addressSingleLine)) {
				// If only a single line address input is provided, then we need to verify that first.
				addressVerification
					.verifyAddressAsSingleLineAsync(addressVerification.addressLocationInput.value.addressSingleLine);
			}
		}
		catch (error: any) {
			console.log(error);
		}
	}

	/**
	 * Saves the insured info after finishing the address verification process.
	 * @param address
	 */
	const finishConfirmAddressVerification = (address: Address): void => {
		// Save the insured info after finishing the address verification process.
		params.props.onSaveInsuredInfo(createInsuredInfoWithMailingAddress(address));
	}

	/**
	 * Creates an insured info record by attempting to attach the property address as the mailing address. 
	 **/
	const createInsuredInfoWithPropertyAddress = (): InsuredInfo => {
		return (propertyAddressAsMailingAddressInput.value ?? false) && params.props.propertyAddress !== undefined
			? createInsuredInfoWithMailingAddress(params.props.propertyAddress)
			: createInsuredInfo();
	}

	/**
	 * Creates an insured info record by attaching the mailing address to it. 
	 * @param mailingAddress
	 */
	const createInsuredInfoWithMailingAddress = (mailingAddress: Address): InsuredInfo => {
		let insuredInfo: InsuredInfo = createInsuredInfo();
		insuredInfo.mailingAddress = mailingAddress;
		return insuredInfo;
	}

	/**
	 * Creates an insured info record by collecting all user inputs.
	 **/
	const createInsuredInfo = (): InsuredInfo => {
		let insuredName: InsuredName = new InsuredName();
		insuredName.firstNameOrCompanyName = insuredNameInput.value;
		insuredName.lastName = insuredLastInput.value;

		let insuredHomePhone: PhoneNumber = new PhoneNumber();
		insuredHomePhone.contactType = PhoneContactType.HOME;
		insuredHomePhone.number = insuredHomePhoneInput.value;

		let insuredMobilePhone: PhoneNumber = new PhoneNumber();
		insuredMobilePhone.contactType = PhoneContactType.MOBILE;
		insuredMobilePhone.number = insuredMobilePhoneInput.value;

		let insuredInfo: InsuredInfo = new InsuredInfo;
		insuredInfo.name = insuredName;
		insuredInfo.phoneNumbers = [insuredHomePhone, insuredMobilePhone];
		insuredInfo.email = insuredEmailInput.value;
		insuredInfo.sendPaymentReceiptToEmail = sendPaymentReceiptToEmailInput.value ?? null;

		return insuredInfo;
	}

	/**
	 * Confirms submission if user chooses to. 
	 **/
	const confirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
		proceedWithFormSubmission();
	}

	/**
	 * Cancels the confirmation prompt for submission. 
	 **/
	const cancelConfirmSubmission = (): void => {
		setOpenSaveConfirmationPopup(false);
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<boolean | undefined>(propertyAddressAsMailingAddressInput, focusToElement)
			&& checkInputValidation<UseInputAddressState>(addressVerification.addressLocationInput, focusToElement)
			&& checkInputValidation<string>(insuredNameInput, focusToElement)
			&& checkInputValidation<string>(insuredLastInput, focusToElement)
			&& checkInputValidation<string>(insuredHomePhoneInput, focusToElement)
			&& checkInputValidation<string>(insuredMobilePhoneInput, focusToElement)
			&& checkInputValidation<string>(insuredEmailInput, focusToElement)
			&& checkInputValidation<boolean | undefined>(sendPaymentReceiptToEmailInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		propertyAddressAsMailingAddressInput.changeShowValidationFlag(true);
		addressVerification.addressLocationInput.changeShowValidationFlag(true);
		insuredNameInput.changeShowValidationFlag(true);
		insuredLastInput.changeShowValidationFlag(true);
		insuredHomePhoneInput.changeShowValidationFlag(true);
		insuredMobilePhoneInput.changeShowValidationFlag(true);
		insuredEmailInput.changeShowValidationFlag(true);
		sendPaymentReceiptToEmailInput.changeShowValidationFlag(true);

	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doesFormHaveChanges = (): boolean => {
		return propertyAddressAsMailingAddressInput.doesInputHaveChanges
			|| addressVerification.addressLocationInput.doesInputHaveChanges
			|| insuredNameInput.doesInputHaveChanges
			|| insuredLastInput.doesInputHaveChanges
			|| insuredHomePhoneInput.doesInputHaveChanges
			|| insuredMobilePhoneInput.doesInputHaveChanges
			|| insuredEmailInput.doesInputHaveChanges
			|| sendPaymentReceiptToEmailInput.doesInputHaveChanges;
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for the mailing address. 
	 **/
	const generateIdForMailingAddress = (): string => {
		return `${generateIdForForm()}-mailingAddress`;
	}

	/**
	 * Generate id for property address as mailing address. 
	 **/
	const generateIdForPropertyAddressAsMailingAddress = (): string => {
		return `${generateIdForForm()}-propertyAddressAsMailingAddress`;
	}

	/**
	 * Generate id for the insured name.
	 **/
	const generateIdForInsuredName = (): string => {
		return `${generateIdForForm()}-insuredName`;
	}

	/**
	 * Generate id for the insured last.
	 **/
	const generateIdForInsuredLast = (): string => {
		return `${generateIdForForm()}-insuredLast`;
	}

	/**
	 * Generate id for the home phone number.
	 **/
	const generateIdForHomePhoneNumber = (): string => {
		return `${generateIdForForm()}-homePhoneNumber`;
	}

	/**
	 * Generate id for the mobile phone number.
	 **/
	const generateIdForMobilePhoneNumber = (): string => {
		return `${generateIdForForm()}-mobilePhoneNumber`;
	}

	/**
	 * Generate id for the email.
	 **/
	const generateIdForEmailNumber = (): string => {
		return `${generateIdForForm()}-email`;
	}

	/**
	 * Generate id for the send payment receipt to email.
	 **/
	const generateIdForSendPaymentReceiptToEmail = (): string => {
		return `${generateIdForForm()}-sendPaymentReceiptToEmail`;
	}

	//#endregion id generators

	// #region input initializers

	/**
	 * Sets up the initial value for the 'mailing address' input.
	 **/
	const setupInitialMailingAddress = (): Address | undefined => {
		let currentMailingAddress: Address | undefined = params.props.insuredInfo?.mailingAddress ?? undefined;
		return currentMailingAddress !== undefined
			&& !AddressService.getInstance().isAddressEmpty(currentMailingAddress)
			? { ...currentMailingAddress }
			: undefined;
	}

	/**
	 * Sets up the initial value for the 'property address is used as the mailing address' input.
	 **/
	const setupInitialPropertyAddressUsedAsMailingAddress = (mailingAddress: Address | undefined): boolean | undefined=> {
		return params.props.propertyAddress !== undefined
			&& mailingAddress !== undefined
			? AddressService.getInstance().doAddressesMatch(mailingAddress, params.props.propertyAddress)
			: undefined;
	}

	/**
	 * Sets up the initial value for the 'home phone' input.
	 **/
	const setupInitialHomePhone = (): string => {
		return params.props.insuredInfo?.phoneNumbers.find((phoneNumber: PhoneNumber) => phoneNumber.contactType === PhoneContactType.HOME)?.number ?? String.empty(); 
	}

	/**
	 * Sets up the initial value for the 'mobile phone' input.
	 **/
	const setupInitialMobilePhone = (): string => {
		return params.props.insuredInfo?.phoneNumbers.find((phoneNumber: PhoneNumber) => phoneNumber.contactType === PhoneContactType.MOBILE)?.number ?? String.empty();
	}

	// #endregion input initializers

	/**
	 * Checks to see if the form requires saving. 
	 **/
	const doesFormRequireSave = (): boolean => {
		return params.props.allowEdits
			&& !areAllInputsDisabled
			&& (doesFormHaveChanges()
				|| params.props.isReviewRequired);
	}

	// #endregion private methods


	// #region variables

	const alwaysSaveAddressOnSubmit: boolean = React.useMemo(() => params.props.alwaysSaveAddressOnSubmit ?? false, [params.props.alwaysSaveAddressOnSubmit]);
	const isCommercial: boolean = React.useMemo(() => params.props.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL, [params.props.broadLineOfBusinessCode]);
	const isInsuredLastNameRequired: boolean = React.useMemo(() => !isCommercial, [isCommercial]);
	const doesPropertyAddressExist: boolean = React.useMemo(() => params.props.propertyAddress !== undefined, [params.props.propertyAddress]);

	const showValidations: boolean = React.useMemo(() => params.props.showValidationsOnMount ?? false, [params.props.showValidationsOnMount]);
	let requireConfirmationOnSave: boolean = params.props.requireConfirmationOnSave ?? false;

	// #region Input settings visibility.

	const showInsuredAddressInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredAddressInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredAddressInput]
	);
	const showInsuredNameInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredNameInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredNameInput]
	);
	const showInsuredLastInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredLastInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredLastInput]
	);
	const showInsuredHomePhoneInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredHomePhoneInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredHomePhoneInput]
	);
	const showInsuredMobilePhoneInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredMobilePhoneInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredMobilePhoneInput]
	);
	const showInsuredEmailInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.insuredEmailInput !== 'hidden',
		[params.props.inputSettingsProps?.insuredEmailInput]
	);
	const showSendPaymentReceiptToEmailInput: boolean = React.useMemo(() =>
		params.props.inputSettingsProps?.sendPaymentReceiptToEmailInput !== 'hidden',
		[params.props.inputSettingsProps?.sendPaymentReceiptToEmailInput]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isInsuredAddressInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showInsuredAddressInput
		|| params.props.inputSettingsProps?.insuredAddressInput === 'disabled',
		[
			params.props.allowEdits,
			showInsuredAddressInput,
			params.props.inputSettingsProps?.insuredAddressInput
		]
	);
	const isPropertyAddressAsMailingAddressInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| isInsuredAddressInputDisabled
		|| !doesPropertyAddressExist,
		[
			params.props.allowEdits,
			isInsuredAddressInputDisabled,
			doesPropertyAddressExist,
		]
	);
	const isInsuredNameInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showInsuredNameInput
		|| params.props.inputSettingsProps?.insuredNameInput === 'disabled',
		[
			params.props.allowEdits,
			showInsuredNameInput,
			params.props.inputSettingsProps?.insuredNameInput
		]
	);
	const isInsuredLastInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !isInsuredLastNameRequired
		|| !showInsuredLastInput
		|| params.props.inputSettingsProps?.insuredLastInput === 'disabled',
		[
			params.props.allowEdits,
			isInsuredLastNameRequired,
			showInsuredLastInput,
			params.props.inputSettingsProps?.insuredLastInput
		]
	);
	const isInsuredHomePhoneInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showInsuredHomePhoneInput
		|| params.props.inputSettingsProps?.insuredHomePhoneInput === 'disabled',
		[
			params.props.allowEdits,
			showInsuredHomePhoneInput,
			params.props.inputSettingsProps?.insuredHomePhoneInput
		]
	);
	const isInsuredMobilePhoneInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showInsuredMobilePhoneInput
		|| params.props.inputSettingsProps?.insuredMobilePhoneInput === 'disabled',
		[
			params.props.allowEdits,
			showInsuredMobilePhoneInput,
			params.props.inputSettingsProps?.insuredMobilePhoneInput
		]
	);
	const isInsuredEmailInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showInsuredEmailInput
		|| params.props.inputSettingsProps?.insuredEmailInput === 'disabled',
		[
			params.props.allowEdits,
			showInsuredEmailInput,
			params.props.inputSettingsProps?.insuredEmailInput
		]
	);
	const isSendPaymentReceiptToEmailInputDisabled: boolean = React.useMemo(() =>
		!params.props.allowEdits
		|| !showSendPaymentReceiptToEmailInput
		|| params.props.inputSettingsProps?.sendPaymentReceiptToEmailInput === 'disabled',
		[
			params.props.allowEdits,
			showSendPaymentReceiptToEmailInput,
			params.props.inputSettingsProps?.sendPaymentReceiptToEmailInput
		]
	);

	// #endregion Input settings disable

	let initialMailingAddress: Address | undefined = setupInitialMailingAddress();
	let initialUsePropertyAddressAsMailingAddress: boolean | undefined = setupInitialPropertyAddressUsedAsMailingAddress(initialMailingAddress);

	// Instantiate the 'property address as mailing address' input hook.
	const propertyAddressAsMailingAddressInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForPropertyAddressAsMailingAddress(),
		label: 'Do you want to use the property address as your mailing address?',
		required: true,
		disabled: isPropertyAddressAsMailingAddressInputDisabled,
		isYesChecked: initialUsePropertyAddressAsMailingAddress,
		showValidation: showValidations,
	});

	// Checks to see if the property address is to be used as the mailing address.
	const isPropertyAddressUsedAsMailingAddress: boolean = React.useMemo(() => propertyAddressAsMailingAddressInput.value ?? false, [propertyAddressAsMailingAddressInput.value]);

	// Instantiate the 'address verification' hook.
	let addressVerification: UseAddressVerificationResults = useAddressVerification({
		addressLocationInputParameters: {
			addressInputParameters: {
				id: generateIdForMailingAddress(),
				label: 'Enter Address, City, State, Zip',
				required: true,
				disabled: isInsuredAddressInputDisabled || isPropertyAddressUsedAsMailingAddress,
				showValidation: showValidations,
				initialInput: {
					address: !initialUsePropertyAddressAsMailingAddress ? initialMailingAddress : undefined,
					addressSingleLine: String.empty(),
				},
				possibleStates: params.props.possibleStates,
				onGetAddressSuggestions: params.props.addressProps.getAddressSuggestions,
			},
		},
		finishConfirmAddressVerification: finishConfirmAddressVerification,
		possibleStates: params.props.possibleStates,
		ignorePossibleStateRiskAllowed: true,
		initialInput: undefined,
		verifySingleLineAddress: params.props.addressProps.verifySingleLineAddress,
		verifyAddress: params.props.addressProps.verifyAddress,
	});


	// Instantiate the 'insured name' input hook.
	const insuredNameInput: UseInputTextResults = useInputText({
		id: generateIdForInsuredName(),
		label: isCommercial ? 'Company Name' : 'First Name',
		required: true,
		disabled: isInsuredNameInputDisabled,
		value: params.props.insuredInfo?.name?.firstNameOrCompanyName ?? String.empty(),
		showValidation: showValidations,
	});

	// Instantiate the 'insured last' input hook.
	const insuredLastInput: UseInputTextResults = useInputText({
		id: generateIdForInsuredLast(),
		label: 'Last Name',
		required: true,
		disabled: isInsuredLastInputDisabled,
		value: params.props.insuredInfo?.name?.lastName ?? String.empty(),
		showValidation: showValidations,
	});

	// Instantiate the 'home phone' input hook.
	const insuredHomePhoneInput: UseInputPhoneResults = useInputPhone({
		id: generateIdForHomePhoneNumber(),
		label: isCommercial ? 'Company Phone' : 'Home Phone',
		required: true,
		disabled: isInsuredHomePhoneInputDisabled,
		value: setupInitialHomePhone(),
		showValidation: showValidations,
	});

	// Instantiate the 'mobile phone' input hook.
	const insuredMobilePhoneInput: UseInputPhoneResults = useInputPhone({
		id: generateIdForMobilePhoneNumber(),
		label: 'Mobile Phone',
		required: false,
		disabled: isInsuredMobilePhoneInputDisabled,
		value: setupInitialMobilePhone(),
		showValidation: showValidations,
	});

	// Instantiate the 'email' input hook.
	const insuredEmailInput: UseInputEmailResults = useInputEmail({
		id: generateIdForEmailNumber(),
		label: 'Email',
		required: true,
		infoText: 'Valid email required and used for digital signatures',
		disabled: isInsuredEmailInputDisabled,
		value: params.props.insuredInfo?.email ?? String.empty(),
		showValidation: showValidations,
	});

	// Instantiate the 'send payment receipt to email' input.
	const sendPaymentReceiptToEmailInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForSendPaymentReceiptToEmail(),
		label: 'Would you like a copy of the payment receipt sent to the insured email address?',
		required: true,
		disabled: isSendPaymentReceiptToEmailInputDisabled,
		isYesChecked: params.props.insuredInfo?.sendPaymentReceiptToEmail ?? false,
		showValidation: showValidations,
	});

	const [openSaveConfirmationPopup, setOpenSaveConfirmationPopup] = React.useState<boolean>(false)
	const [showStatusMessage, setShowStatusMessage] = React.useState<boolean>(false);

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return propertyAddressAsMailingAddressInput.isDisabled
			&& addressVerification.addressLocationInput.isDisabled
			&& insuredNameInput.isDisabled
			&& insuredLastInput.isDisabled
			&& insuredHomePhoneInput.isDisabled
			&& insuredMobilePhoneInput.isDisabled
			&& insuredEmailInput.isDisabled
			&& sendPaymentReceiptToEmailInput.isDisabled;
	}, [
		propertyAddressAsMailingAddressInput.isDisabled,
		addressVerification.addressLocationInput.isDisabled,
		insuredNameInput.isDisabled,
		insuredLastInput.isDisabled,
		insuredHomePhoneInput.isDisabled,
		insuredMobilePhoneInput.isDisabled,
		insuredEmailInput.isDisabled,
		sendPaymentReceiptToEmailInput.isDisabled,
	]);

	// #endregion variables


	// #region life-cycle methods.

	// Called when changes are made to the 'property address as mailing address' value.
	React.useEffect(() => {
		// If the property address is to be used.
		if (propertyAddressAsMailingAddressInput.doesInputHaveChanges
			&& isPropertyAddressUsedAsMailingAddress) {
			// Remove the current address input.
			addressVerification.addressLocationInput.setInput({
				address: undefined,
				addressSingleLine: String.empty(),
			});
			// Remove any verified address results.
			addressVerification.setVerifiedAddressResult(undefined);
		}
	}, [propertyAddressAsMailingAddressInput.value]);

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!showValidations
			|| checkFormValidation(true)) {
			if (doesPropertyAddressExist) {
				// If validations pass or are not to be shown, then initially the first input should be focused.
				propertyAddressAsMailingAddressInput.focusToInput();
			}
			else {
				addressVerification.addressLocationInput.focusToInput();
			}
		}
	}, []);

	// Called when updates are made the props related to the status of the form.
	React.useEffect(() => {
		// Determine if the status message should be rendered.
		if (params.props.isReviewRequired
			&& !String.isNullOrWhiteSpace(params.props.statusMessage)
			&& params.props.allowEdits
			&& checkFormValidation(false)) {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return true;
			});
		}
		else {
			setShowStatusMessage((currentShowStatusMessage: boolean) => {
				return false;
			});
		}
	}, [params.props.isReviewRequired, params.props.statusMessage, params.props.allowEdits]);

	// #endregion life-cycle methods.

	return {
		areAllInputsDisabled,
		isPropertyAddressUsedAsMailingAddress,
		doesPropertyAddressExist,
		propertyAddressAsMailingAddressInput,
		addressVerification,
		submitForm,
		applyFormProps,
		applySubformInsuredInfoMailingAddressProps,
		applySubformInsuredInfoContactProps,
		applyNavigationFooterProps,
		applySubmissionFormPopupConfirmationProps,
	};
}

// Export parameters.
export {
	UseFormInsuredInfoParameters,
}

// Export results.
export {
	UseFormInsuredInfoResults,
}

export default useFormInsuredInfo;